import { Inject, Injectable, OnDestroy } from '@angular/core';
import { filter, Subject, firstValueFrom, take } from 'rxjs';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
  SilentRequest,
  AccountInfo
} from '@azure/msal-browser';
import { env } from '../../../../environments/environment-loader';

@Injectable({ providedIn: 'root' })
export class MsalOidcAuthService implements OnDestroy {
  private readonly destroying$ = new Subject<void>();
  private loginStarted = false;
  private readonly initPromise: Promise<void>;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    // Initialize MSAL instance
    this.initPromise = this.initializeMsal();

    // React to account add/remove ONCE
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        filter(() => !this.destroying$.isStopped)
      )
      .subscribe(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length === 0) {
          // No accounts → return to app root (or a signed-out landing)
          window.location.pathname = '/';
        }
      });

    // When interactions complete, ensure an active account is set
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        filter(() => !this.destroying$.isStopped)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  /** Wait until no other MSAL interaction is happening */
  private async waitForNoInteraction(): Promise<void> {
    const status = this.msalService.instance.getLogger
      ? undefined
      : undefined; // (kept to illustrate no-op; we rely on broadcast below)

    await firstValueFrom(
      this.msalBroadcastService.inProgress$.pipe(
        filter((s) => s === InteractionStatus.None)
      )
    );
  }

  /** Ensure we always have an active account set for silent token acquisition */
  checkAndSetActiveAccount(): void {
    const active = this.msalService.instance.getActiveAccount();
    if (!active) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
    }
  }

  /** Await MSAL bootstrap (init + redirect handling) before auth flows. */
  async ensureInitialized(): Promise<void> {
    await this.initPromise;
  }

  /** One-time MSAL bootstrap */
  private async initializeMsal(): Promise<void> {
    await this.msalService.instance.initialize();
  }

  /** wait for msal interactions to complete */
  private async waitForInteractionNone(): Promise<void> {
    await firstValueFrom(
      this.msalBroadcastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1)
      )
    );
  }

  async waitForMsalCacheClear(): Promise<void> {
    await this.msalService.instance.clearCache();
  }

  /**
   * Login if needed (redirect), otherwise return a silent token for default scopes.
   * Returns null when a redirect navigation is triggered.
   */
  async loginRedirect(): Promise<AuthenticationResult | null> {
    await this.ensureInitialized();
    await this.waitForInteractionNone();

    const accounts = this.msalService.instance.getAllAccounts();

    // If not signed in → start login redirect exactly once
    if (accounts.length === 0) {
      if (this.loginStarted) {
        return null;
      }
      this.loginStarted = true;
      try {
        await this.waitForNoInteraction();
        await this.msalService.instance.loginRedirect({
          ...(this.msalGuardConfig.authRequest as RedirectRequest),
          redirectUri: this.MSAL_REDIRECT_URI()
        });
        return null; // navigation occurs
      } finally {
        // In practice this line won't run after navigation, but is safe for cases where login fails before redirect
        this.loginStarted = false;
      }
    }

    // Already signed in → acquire silent token for default scopes
    const active = this.msalService.instance.getActiveAccount() ?? accounts[0];
    const defaultScopes = ['openid'];

    return this.msalService.instance.acquireTokenSilent({
      scopes: defaultScopes,
      account: active
    } as SilentRequest);
  }

  /**
   * Sign out with redirect. Keep postLogoutRedirectUri consistent with app registration if set.
   */
  logout(): void {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: null, // or this.MSAL_REDIRECT_URI()
      idTokenHint: this.msalService.instance.getActiveAccount()?.idToken
    });
  }

  clearCache(): void {
    this.msalService.instance.clearCache();
  }

  /**
   * Centralized redirect URI (must exactly match a registered Redirect URI in AAD)
   */
  MSAL_REDIRECT_URI(): string {
    const hostName = env.envName ?? '';
    if (hostName.includes('dev1')) {
      return 'https://dev1-ebaccount.thehartford.com/'; // must be registered (note trailing slash)
    } else if (hostName.includes('sqa1')) {
      return 'https://sqa1-ebaccount.thehartford.com/'; // must be registered (note trailing slash)
    } else if (hostName.includes('qa1')) {
      return 'https://qa1-ebaccount.thehartford.com/'; // must be registered (note trailing slash)
    } else if (hostName.includes('pt1')) {
      return 'https://pt1-ebaccount.thehartford.com/'; // must be registered (note trailing slash)
    } else if (hostName.includes('prod')) {
      return 'https://gbaccount.thehartford.com/'; // must be registered (note trailing slash)
    } else if (hostName.includes('localhost')) {
      return 'http://localhost.thehartford.com:4200/'; // must be registered
    } else {
      // default/fallback environment
      return 'https://dev1-ebaccount.thehartford.com/';
    }
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
